import AsyncStorage from '@react-native-async-storage/async-storage';
import { gql } from '@apollo/client';

import { getRandomString } from '../../common/utils/utils';

const STORAGE_KEY = '@sentry/offlineQueue/v1';

// After this many non-network failures an entry stops auto-retrying and waits
// for the user to retry or remove it from the sync banner.
const MAX_AUTO_RETRY_FAILURES = 5;

/**
 * Check if an error is a network-related error that should pause queue processing.
 * Network errors indicate the device is offline or can't reach the server,
 * so we should stop trying to flush the queue until connectivity is restored.
 *
 * @param {Error} error - The error to check
 * @returns {boolean} True if this is a network error
 */
const isNetworkError = (error) => {
	if (!error) {
		return false;
	}

	if (error.networkError) {
		return true;
	}

	return typeof error.message === 'string' && (
		error.message.toLowerCase().includes('network') ||
		error.message.toLowerCase().includes('offline') ||
		error.message.toLowerCase().includes('failed to fetch')
	);
};

/**
 * Manages an offline queue for GraphQL mutations.
 *
 * This class provides:
 * - Persistent storage of queued mutations in AsyncStorage
 * - Automatic retry when connectivity is restored
 * - Operation-specific configuration for serialization and retry logic
 * - Attachment handling for images and other files
 * - Optimistic updates to Apollo cache
 *
 * Queue entries are processed sequentially (FIFO) to maintain operation order.
 * Network failures pause processing until connectivity changes; validation
 * failures are skipped past (and eventually paused) so they never block the
 * rest of the queue.
 */
export default class OfflineQueueManager {
	constructor ({ storageKey = STORAGE_KEY } = {}) {
		this.storageKey = storageKey;
		this.queue = [];
		this.isInitialized = false;
		this.isOnline = true;
		this.isFlushing = false;
		this.listeners = new Set();
	}

	/**
	 * Initialize the queue manager by loading persisted queue from storage.
	 * Should be called once when the app starts.
	 */
	async initialize () {
		if (this.isInitialized) {
			return;
		}

		try {
			const rawQueue = await AsyncStorage.getItem(this.storageKey);
			if (rawQueue) {
				const parsedQueue = JSON.parse(rawQueue);
				if (Array.isArray(parsedQueue)) {
					this.queue = parsedQueue.map(entry => ({
						failureCount: 0,
						lastError: null,
						lastAttemptAt: null,
						...entry,
					}));
				}
			}
		}
		catch (error) {
			// Failed to load queue from storage, start with empty queue
			// This can happen on first install or if storage is corrupted
			console.error('[OfflineQueue] Failed to initialize queue:', error);
			this.queue = [];
		}
		finally {
			this.isInitialized = true;
			this.notifyListeners();
		}
	}

	getQueue () {
		return this.queue;
	}

	subscribe (listener) {
		this.listeners.add(listener);

		return () => {
			this.listeners.delete(listener);
		};
	}

	setOnline (isOnline) {
		this.isOnline = isOnline;
	}

	async enqueue (entry) {
		const offlineEntry = {
			id: entry.id || getRandomString(24),
			configKey: entry.configKey,
			document: entry.document,
			operationName: entry.operationName,
			variables: entry.variables || {},
			context: entry.context || {},
			attachments: entry.attachments || [],
			meta: entry.meta || {},
			createdAt: entry.createdAt || Date.now(),
			failureCount: entry.failureCount || 0,
			lastError: entry.lastError || null,
			lastAttemptAt: entry.lastAttemptAt || null,
		};

		// A visit has one note: a correction supersedes even failed queued text.
		if (offlineEntry.configKey === 'setServiceDocketServiceNote' && offlineEntry.variables.id) {
			this.queue = this.queue.filter(queuedEntry => (
				queuedEntry.configKey !== offlineEntry.configKey ||
				queuedEntry.variables?.id !== offlineEntry.variables.id
			));
		}

		this.queue.push(offlineEntry);
		await this.persist();
		this.notifyListeners();

		return offlineEntry;
	}

	async remove (entryId) {
		const initialLength = this.queue.length;
		this.queue = this.queue.filter(entry => entry.id !== entryId);

		if (this.queue.length !== initialLength) {
			await this.persist();
			this.notifyListeners();
		}
	}

	async clear () {
		this.queue = [];
		await this.persist();
		this.notifyListeners();
	}

	/**
	 * Process the offline queue by executing mutations against the server.
	 *
	 * Queue processing:
	 * - Entries are processed sequentially (FIFO order)
	 * - Each entry is configured via the operation config (configs parameter)
	 * - Network errors (including HTTP 5xx, which Apollo reports as
	 *   networkError) stop processing until connectivity changes
	 * - Non-network errors (validation/GraphQL) mark the entry failed but do
	 *   NOT block the rest of the queue — one bad entry must not stop photos
	 *   and confirmations for other machines from syncing
	 * - Entries past MAX_AUTO_RETRY_FAILURES wait for a manual retry/remove
	 * - The isFlushing flag prevents concurrent flush operations
	 *
	 * @param {ApolloClient} client - Apollo client for executing mutations
	 * @param {Object} configs - Operation configs keyed by configKey
	 * @returns {Promise<void>}
	 */
	async flush (client, configs = {}) {
		if (!this.isInitialized || this.isFlushing || !this.isOnline || !client) {
			return;
		}

		this.isFlushing = true;

		try {
			const attemptedEntries = new Set();
			let entry;

			// Enqueue can replace an entry while its request is in flight.
			while ((entry = this.queue.find(queuedEntry => !attemptedEntries.has(queuedEntry)))) {
				attemptedEntries.add(entry);
				const config = configs[entry.configKey];

				if ((entry.failureCount || 0) >= MAX_AUTO_RETRY_FAILURES) {
					continue;
				}

				try {
					const mutation = config?.getMutation
						? config.getMutation(entry)
						: gql(entry.document);

					const variables = config?.prepareVariables
						? await config.prepareVariables({ entry, client })
						: entry.variables;

					const context = config?.prepareContext
						? await config.prepareContext({ entry, client })
						: entry.context;

					const result = await client.mutate({
						mutation,
						variables,
						context,
					});

					if (config?.onSuccess) {
						await config.onSuccess({ entry, client, result });
					}

					await this.remove(entry.id);
				}
				catch (error) {
					if (isNetworkError(error)) {
						// Offline again (or the server is unreachable) — leave the
						// queue untouched and resume on the next connectivity change.
						break;
					}

					if (config?.onError) {
						const behaviour = await config.onError({ entry, client, error });

						if (behaviour === 'drop') {
							await this.remove(entry.id);
							continue;
						}

						if (behaviour === 'retry') {
							attemptedEntries.delete(entry);
							continue;
						}
					}

					// Skip past the failed entry so the rest of the queue still syncs.
					await this.markEntryFailed(entry, error);
				}
			}
		}
		finally {
			this.isFlushing = false;
		}
	}

	/**
	 * Reset failure counters so a manual "Sync now" retries entries that were
	 * paused after repeated non-network failures.
	 */
	async resetFailures () {
		let changed = false;
		this.queue.forEach((entry) => {
			if (entry.failureCount) {
				entry.failureCount = 0;
				entry.lastError = null;
				changed = true;
			}
		});

		if (changed) {
			await this.persist();
			this.notifyListeners();
		}
	}

	/**
	 * Mark an entry as failed due to a non-network error so the UI can surface it and
	 * the user can decide whether to retry or drop it.
	 */
	async markEntryFailed (entry, error) {
		const failedEntry = entry || {};
		failedEntry.failureCount = (failedEntry.failureCount || 0) + 1;
		failedEntry.lastError = error?.message || 'Unknown error';
		failedEntry.lastAttemptAt = Date.now();
		await this.persist();
		this.notifyListeners();
	}

	async persist () {
		try {
			await AsyncStorage.setItem(this.storageKey, JSON.stringify(this.queue));
		}
		catch (error) {
			// Critical: Failed to persist queue to storage
			// Queue is still in memory but won't survive app restart
			console.error('[OfflineQueue] Failed to persist queue to storage:', error);
		}
	}

	notifyListeners () {
		this.listeners.forEach(listener => {
			try {
				listener(this.queue);
			}
			catch (error) {
				// Listener threw an error, log but continue notifying other listeners
				console.error('[OfflineQueue] Listener error:', error);
			}
		});
	}
}
